import { useMemo, useRef } from 'react';

import type { AnchorRefs } from './types';

/**
 * When virtualization is enabled, the absolute-positioned older-groups
 * container starts with estimated heights and grows as `measureElement`
 * settles real heights. The default mount-time `scrollToBottom` from
 * `useSmoothScrollAnchor` fires before measurement completes, so the scroll
 * position ends up near the top of the (now-larger) container — making the
 * chat appear to open at the first message instead of the most recent one.
 *
 * The treatment keeps the bottom anchored during a short settling window while
 * async content such as Smart Links resolves, then locks in. The hook depends
 * on `totalSize`, so late measurement changes intentionally restart that
 * window and give the newly grown content another chance to anchor before
 * settling. Subsequent user-initiated upward scrolling is not snapped back.
 * The window also resets when the conversation changes or when the parent
 * bumps `scrollToBottomGeneration` (e.g. after the user sends a new message).
 *
 * Calls `container.scrollTo` directly because `useSmoothScrollAnchor` captures
 * `containerRef.current` on first render (when it's still null), so its
 * callback is a no-op on the very first mount.
 */
export const useInitialBottomAnchorRefs = (): AnchorRefs => {
	const hasInitialScrollSettledRef = useRef(false);
	const lastSettledConversationIdRef = useRef<string | null>(null);
	const lastScrollToBottomGenerationRef = useRef(0);

	return useMemo(
		() => ({
			hasInitialScrollSettledRef,
			lastSettledConversationIdRef,
			lastScrollToBottomGenerationRef,
		}),
		[],
	);
};
